import type {RefObject} from 'react';
import type {RGBA} from '@Utils/color.util';
import type {QRSocialPlatform} from '@Libraries/qr-code/qr-social-platform.library';
import {hexToRgba, PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import type {
  QRAppStoreInput,
  QRCalendarEventInput,
  QREmailInput,
  QRMultimediaInput,
  QRPaymentLinkInput,
  QRPayloadIssue,
  QRPhoneInput,
  QRSmsInput,
  QRSocialInput,
  QRTextInput,
  QRUrlInput,
  QRVCardInput,
  QRWifiInput,
} from '@Libraries/qr-code/qr-code.types';
import {QRPayloadType, QRPayloadVariant, QRWifiSecurity} from '@Libraries/qr-code/qr-code.types';
import {
  QRCodeDotType,
  QRCodeEyeBallType,
  QRCodeEyeFrameType,
  QRCornerStyle,
  QR_CORNER_PREVIEW_FILENAMES,
  QR_CORNER_STYLE_PRESETS,
  QR_DEFAULT_BACKGROUND_COLOR,
  QR_DEFAULT_BACKGROUND_CORNER_RADIUS,
  QR_DEFAULT_FOREGROUND_COLOR,
  QR_DEFAULT_MARGIN,
  QR_PATTERN_PREVIEW_FILENAMES,
} from '@Libraries/qr-code/qr-code.types';
import {getQRDefaultUtcOffsetMinutes, getQRUtcOffsetLabel, QR_UTC_OFFSET_MINUTES} from '@Libraries/qr-code/qr-payload-encoding.library';
import type {QRSelectOption} from '@Components/qr-code-generator/components/qr-select/qr-select.types';
import type {QRCodeFileExtension} from '@Libraries/qr-code/qr-code.types';

export const QR_CODE_GENERATOR_CONTAINER_ID = 'qr-code-generator';

export const QR_TEMPLATE_TILES_DATA_ATTRIBUTE = 'templateTiles';
export const QR_DEFAULT_TYPE_DATA_ATTRIBUTE = 'defaultQrType';
export const QR_SOCIAL_ICON_BASE_URL_DATA_ATTRIBUTE = 'socialIconBaseUrl';

export const QR_PREVIEW_SIZE_PX = 512;
export const QR_PREVIEW_DISPLAY_SIZE_PX = 240;

export const QR_SAMPLE_PAYLOAD = 'https://www.postermywall.com';

/** The design system's tertiary ink: the sample code is drawn in it so an untouched tool reads as disabled rather than ready. */
export const QR_SAMPLE_FOREGROUND_COLOR: RGBA = hexToRgba(PMW_COLORS_NEUTRAL.NEUTRAL_28);

const QR_LOGO_DISPLAY_SIZE_PX = 28;
export const QR_LOGO_SIZE_RATIO = QR_LOGO_DISPLAY_SIZE_PX / QR_PREVIEW_DISPLAY_SIZE_PX;
export const QR_PREVIEW_DEBOUNCE_MS = 150;

/** Long enough to collapse a burst of typing into one write, short enough that a reload mid-sentence loses nothing anyone would notice. */
export const QR_SESSION_WRITE_DEBOUNCE_MS = 400;

const QR_DOWNLOAD_FILENAME = 'postermywall-qr-code';

export const QR_DOWNLOAD_FILENAME_BY_VARIANT: Record<QRPayloadVariant, string> = {
  [QRPayloadVariant.DEFAULT]: QR_DOWNLOAD_FILENAME,
  [QRPayloadVariant.APP_STORE]: `${QR_DOWNLOAD_FILENAME}-app-store`,
  [QRPayloadVariant.PLAY_STORE]: `${QR_DOWNLOAD_FILENAME}-google-play`,
};
export const QR_LOGO_ACCEPTED_FILE_TYPES = 'image/png,image/jpeg,image/svg+xml,image/webp';
export const QR_LOGO_MAX_FILE_BYTES = 5 * 1024 * 1024;

export const LOGO_BEARING_TYPES: QRPayloadType[] = [QRPayloadType.SOCIAL, QRPayloadType.PAYMENT_LINK];

export const QR_TOOL_TYPES: QRPayloadType[] = [
  QRPayloadType.URL,
  QRPayloadType.TEXT,
  QRPayloadType.PHONE,
  QRPayloadType.VCARD,
  QRPayloadType.EMAIL,
  QRPayloadType.SOCIAL,
  QRPayloadType.SMS,
  QRPayloadType.WIFI,
  QRPayloadType.CALENDAR_EVENT,
  QRPayloadType.PAYMENT_LINK,
  QRPayloadType.APP_STORE,
  QRPayloadType.MULTIMEDIA,
];

export interface QRDesignTile {
  name: string;
  designHashedId: string;
  thumbnailUrl: string;
  width?: number;
  height?: number;
}

export type QRDesignTileSets = Partial<Record<QRPayloadType, QRDesignTile[]>>;

export interface QRStyleOption {
  labelKey: string;
  override: Partial<QRCodeStyle>;
  previewFileName: string;
}

const QR_DOT_STYLE_LABEL_KEYS: Record<QRCodeDotType, string> = {
  [QRCodeDotType.SQUARE]: 'pmwjs_square',
  [QRCodeDotType.ROUNDED]: 'pmwjs_rounded',
  [QRCodeDotType.DOTS]: 'pmwjs_qr_dots',
  [QRCodeDotType.CLASSY]: 'pmwjs_qr_smooth',
  [QRCodeDotType.CLASSY_ROUNDED]: 'pmwjs_qr_smooth_rounded',
  [QRCodeDotType.EXTRA_ROUNDED]: 'pmwjs_qr_extra_rounded',
};

const QR_CORNER_STYLE_LABEL_KEYS: Record<QRCornerStyle, string> = {
  [QRCornerStyle.SQUARE]: 'pmwjs_square',
  [QRCornerStyle.ROUNDED]: 'pmwjs_rounded',
  [QRCornerStyle.ROUNDED_CIRCLE]: 'pmwjs_qr_rounded_circle',
  [QRCornerStyle.CIRCLE]: 'pmwjs_qr_circle',
};

export const QR_DOT_STYLE_OPTIONS: QRStyleOption[] = [
  QRCodeDotType.SQUARE,
  QRCodeDotType.ROUNDED,
  QRCodeDotType.DOTS,
  QRCodeDotType.CLASSY,
  QRCodeDotType.CLASSY_ROUNDED,
  QRCodeDotType.EXTRA_ROUNDED,
].map((dotStyle): QRStyleOption => {
  return {labelKey: QR_DOT_STYLE_LABEL_KEYS[dotStyle], override: {dotStyle}, previewFileName: QR_PATTERN_PREVIEW_FILENAMES[dotStyle]};
});

export const QR_CORNER_STYLE_OPTIONS: QRStyleOption[] = QR_CORNER_STYLE_PRESETS.map((preset): QRStyleOption => {
  return {
    labelKey: QR_CORNER_STYLE_LABEL_KEYS[preset.id],
    override: {eyeFrameStyle: preset.eyeFrameStyle, eyeBallStyle: preset.eyeBallStyle},
    previewFileName: QR_CORNER_PREVIEW_FILENAMES[preset.id],
  };
});

export const QR_UTC_OFFSET_OPTIONS: QRSelectOption[] = QR_UTC_OFFSET_MINUTES.map((offsetMinutes): QRSelectOption => {
  return {value: String(offsetMinutes), label: getQRUtcOffsetLabel(offsetMinutes)};
});

export const QR_OUTLINE_THICKNESS_OFF = 0;

export const QR_OUTLINE_THICKNESS_MIN = 1;
export const QR_OUTLINE_THICKNESS_MAX = 20;

export const QR_DEFAULT_DOWNLOAD_EXTENSION: QRCodeFileExtension = 'png';

export const QR_CLIPBOARD_IMAGE_MIME_TYPE = 'image/png';
export const QR_ACTION_FEEDBACK_MS = 2000;

export enum QRGeneratorActionKind {
  SET_ACTIVE_TYPE = 'setActiveType',
  PATCH_URL = 'patchUrl',
  PATCH_SOCIAL = 'patchSocial',
  PATCH_TEXT = 'patchText',
  PATCH_EMAIL = 'patchEmail',
  PATCH_PHONE = 'patchPhone',
  PATCH_SMS = 'patchSms',
  PATCH_WIFI = 'patchWifi',
  PATCH_VCARD = 'patchVCard',
  PATCH_CALENDAR_EVENT = 'patchCalendarEvent',
  PATCH_PAYMENT_LINK = 'patchPaymentLink',
  PATCH_APP_STORE = 'patchAppStore',
  PATCH_MULTIMEDIA = 'patchMultimedia',
  PATCH_STYLE = 'patchStyle',
  SET_LOGO = 'setLogo',
}

export const getDefaultQRCodeStyle = (): QRCodeStyle => {
  return {
    foregroundColor: QR_DEFAULT_FOREGROUND_COLOR,
    backgroundColor: QR_DEFAULT_BACKGROUND_COLOR,
    dotStyle: QRCodeDotType.SQUARE,
    eyeFrameStyle: QRCodeEyeFrameType.SQUARE,
    eyeBallStyle: QRCodeEyeBallType.SQUARE,
    marginPercent: QR_DEFAULT_MARGIN,
    backgroundCornerRadiusPercent: QR_DEFAULT_BACKGROUND_CORNER_RADIUS,
    outlineThickness: QR_OUTLINE_THICKNESS_OFF,
    outlineColor: QR_DEFAULT_FOREGROUND_COLOR,
  };
};

export interface QRGeneratorLogo {
  file: File;
  dataUrl: string;
}

export interface QRGeneratorState {
  activeType: QRPayloadType;
  urlInput: QRUrlInput;
  socialInput: QRSocialInput;
  textInput: QRTextInput;
  emailInput: QREmailInput;
  phoneInput: QRPhoneInput;
  smsInput: QRSmsInput;
  wifiInput: QRWifiInput;
  vCardInput: QRVCardInput;
  calendarEventInput: QRCalendarEventInput;
  paymentLinkInput: QRPaymentLinkInput;
  appStoreInput: QRAppStoreInput;
  multimediaInput: QRMultimediaInput;
  sharedContactDetails: QRSharedContactDetails;
  style: QRCodeStyle;
  logo: QRGeneratorLogo | null;
}

export interface QRSharedContactDetails {
  phoneNumber: string;
  emailAddress: string;
  website: string;
}

export type QRGeneratorAction =
  | {kind: QRGeneratorActionKind.SET_ACTIVE_TYPE; activeType: QRPayloadType}
  | {kind: QRGeneratorActionKind.PATCH_URL; patch: Partial<QRUrlInput>}
  | {kind: QRGeneratorActionKind.PATCH_SOCIAL; patch: Partial<QRSocialInput>}
  | {kind: QRGeneratorActionKind.PATCH_TEXT; patch: Partial<QRTextInput>}
  | {kind: QRGeneratorActionKind.PATCH_EMAIL; patch: Partial<QREmailInput>}
  | {kind: QRGeneratorActionKind.PATCH_PHONE; patch: Partial<QRPhoneInput>}
  | {kind: QRGeneratorActionKind.PATCH_SMS; patch: Partial<QRSmsInput>}
  | {kind: QRGeneratorActionKind.PATCH_WIFI; patch: Partial<QRWifiInput>}
  | {kind: QRGeneratorActionKind.PATCH_VCARD; patch: Partial<QRVCardInput>}
  | {kind: QRGeneratorActionKind.PATCH_CALENDAR_EVENT; patch: Partial<QRCalendarEventInput>}
  | {kind: QRGeneratorActionKind.PATCH_PAYMENT_LINK; patch: Partial<QRPaymentLinkInput>}
  | {kind: QRGeneratorActionKind.PATCH_APP_STORE; patch: Partial<QRAppStoreInput>}
  | {kind: QRGeneratorActionKind.PATCH_MULTIMEDIA; patch: Partial<QRMultimediaInput>}
  | {kind: QRGeneratorActionKind.PATCH_STYLE; patch: Partial<QRCodeStyle>}
  | {kind: QRGeneratorActionKind.SET_LOGO; logo: QRGeneratorLogo | null};

export const getInitialQRGeneratorState = (generatedAt: Date, activeType: QRPayloadType = QRPayloadType.URL): QRGeneratorState => {
  return {
    activeType,
    urlInput: {link: ''},
    socialInput: {link: '', whatsAppMessage: ''},
    textInput: {text: ''},
    emailInput: {emailAddress: '', subject: '', body: ''},
    phoneInput: {phoneNumber: ''},
    smsInput: {phoneNumber: '', message: ''},
    wifiInput: {ssid: '', security: QRWifiSecurity.WPA, password: ''},
    vCardInput: {
      firstName: '',
      lastName: '',
      organisation: '',
      jobTitle: '',
      mobileNumber: '',
      workNumber: '',
      faxNumber: '',
      emailAddress: '',
      website: '',
      street: '',
      city: '',
      region: '',
      postalCode: '',
      country: '',
    },
    calendarEventInput: {
      title: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      utcOffsetMinutes: getQRDefaultUtcOffsetMinutes(generatedAt),
      location: '',
      description: '',
      generatedAt,
    },
    paymentLinkInput: {link: ''},
    appStoreInput: {appStoreLink: '', playStoreLink: ''},
    multimediaInput: {link: ''},
    sharedContactDetails: {phoneNumber: '', emailAddress: '', website: ''},
    style: getDefaultQRCodeStyle(),
    logo: null,
  };
};

export interface QRCodeGeneratorProps {
  socialIconBaseUrl: string;
  defaultActiveType: QRPayloadType;
  templateTileSets: QRDesignTileSets;
}

export interface QRResolvedPlatformLogo {
  platform: QRSocialPlatform;
  dataUrl: string;
}

export interface QRCodePreviews {
  previewUrls: (string | null)[];
  isRendering: boolean;
}

export interface QRCodeStyle {
  foregroundColor: RGBA;
  backgroundColor: RGBA;
  dotStyle: QRCodeDotType;
  eyeFrameStyle: QRCodeEyeFrameType;
  eyeBallStyle: QRCodeEyeBallType;
  marginPercent: number;
  backgroundCornerRadiusPercent: number;
  outlineThickness: number;
  outlineColor: RGBA;
}

export interface QRPreviewStyle extends QRCodeStyle {
  logoDataUrl: string | null;
}

export enum ScrollAxis {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

export interface ScrollAffordanceState {
  scrollableRef: RefObject<HTMLDivElement | null>;
  canScrollBack: boolean;
  canScrollForward: boolean;
  scrollBack: () => void;
  scrollForward: () => void;
}

export type QRRasterInputs = Omit<QRRenderInputs, 'logoDataUrl'>;

export interface QRRasterRequest {
  renderInputs: QRRasterInputs[];
  logoDataUrl: string | null;
  debounceMs: number;
}

export enum QRCopyState {
  IDLE = 'idle',
  COPIED = 'copied',
  FAILED = 'failed',
}

export interface QRRenderInputs extends QRPreviewStyle {
  payload: string;
  sizePx: number;
}

export interface QRFormProps<TInput> {
  input: TInput;
  issues: QRPayloadIssue[];
  onPatch: (patch: Partial<TInput>) => void;
}
